// src/automation/useAutoPermissions.js
//
// Resolves the signed-in user's Automation role and exposes capability helpers.
//   roles: admin | contributor | viewer | no_access
//     admin        -> canAdmin, canManageUsers, canEditTask(*) = true
//     contributor  -> canEditTask(name) only for allowed_tasks
//     viewer        -> read-only (canEditTask always false)
//     no_access     -> read-only too (we still let them view; tighten later)
//
// The role comes from the dispatcher (get_permissions), keyed by the user's
// login from the id_token. The UI gates admin screens + edit controls on this.

import { useQuery } from "@tanstack/react-query";
import { useUser } from "shared/context/UserContext";
import { getAutoPermissions } from "./api/automation_data";

export function useAutoPermissions({ enabled = true } = {}) {
  const user = useUser();
  const login = user?.userId || "";

  const { data, isLoading } = useQuery({
    queryKey: ["autoPermissions", login],
    queryFn: () => getAutoPermissions(login),
    // Caller can defer this (e.g. until a bootstrap call has seeded the cache)
    // to avoid a duplicate permissions request on first load.
    enabled: !!login && enabled,
    staleTime: 5 * 60 * 1000,
  });

  const role = data?.role || "no_access";
  const allowed = data?.allowed_tasks || [];
  const isAdmin = role === "admin";
  const isContributor = role === "contributor";

  const canEditTask = (taskName) => {
    if (isAdmin) return true;
    if (isContributor) return allowed.includes(taskName);
    return false;
  };

  // Role model (enforced server-side too):
  //   admin       — hardcoded in the Lambda (sinnikhy). Everything incl. Auth,
  //                 delete any automation, Activity Log.
  //   contributor — Admin panel (Access + Automations), add automations,
  //                 manage users within their tasks, edit/run their tasks.
  //   viewer      — read-only.
  const canManage = isAdmin || isContributor;
  return {
    loading: isLoading,
    role,
    allowedTasks: allowed,
    isOwner: !!data?.is_owner,
    isAdmin,
    isContributor,
    isViewer: role === "viewer",
    hasAccess: role !== "no_access",
    canAdmin: canManage,          // can open the Admin panel
    canManageUsers: canManage,    // Access tab
    canAddAutomation: canManage,
    canDelete: isAdmin,
    canViewActivity: isAdmin,
    canEditTask,                  // edit config/map/report design + run/test
  };
}
